import { useEffect, useRef, useCallback, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Phase 8A — Self-hosted analytics tracker.
 *
 * Respects the cookie consent categories.analytics flag (writes are
 * gated on true). Beacon POST to `/api/analytics/{pageview,event}` —
 * no third-party vendor. session_id is UUID v4 kept in localStorage
 * (`glms_session_analytics`) with a soft 30-min inactivity rotation.
 *
 * Public API exposed via <TrackerContext>:
 *   const { trackEvent } = useAnalytics();
 *   trackEvent("cta_click", { cta: "start-free" });
 */

const TrackerContext = createContext({ trackEvent: () => {} });

const CONSENT_KEY = "glms_cookie_consent";
const SESSION_KEY = "glms_session_analytics";
const SESSION_TS_KEY = "glms_session_analytics_ts";
const SESSION_MAX_IDLE_MS = 30 * 60 * 1000; // 30 min

function uuidv4() {
    // RFC4122 v4 without external deps
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function hasAnalyticsConsent() {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        return Boolean(parsed?.categories?.analytics);
    } catch {
        return false;
    }
}

function getSessionId() {
    const now = Date.now();
    let sid = localStorage.getItem(SESSION_KEY);
    const ts = parseInt(localStorage.getItem(SESSION_TS_KEY) || "0", 10);
    if (!sid || !ts || now - ts > SESSION_MAX_IDLE_MS) {
        sid = uuidv4();
        localStorage.setItem(SESSION_KEY, sid);
    }
    localStorage.setItem(SESSION_TS_KEY, String(now));
    return sid;
}

function backendBase() {
    const u = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${u}/api/analytics`;
}

async function beacon(path, payload) {
    if (!hasAnalyticsConsent()) return; // hard gate
    const url = `${backendBase()}/${path}`;
    const body = JSON.stringify(payload);
    // navigator.sendBeacon has better delivery on tab close
    try {
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            const blob = new Blob([body], { type: "application/json" });
            const ok = navigator.sendBeacon(url, blob);
            if (ok) return;
        }
    } catch {}
    // Fallback: fetch keepalive (with credentials so user_id resolves)
    try {
        await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
            credentials: "include",
        });
    } catch {
        // silent: analytics never breaks the app
    }
}

export function useAnalytics() {
    return useContext(TrackerContext);
}

export default function AnalyticsTracker({ children }) {
    const location = useLocation();
    const { user } = useAuth() || {};
    const lastPathRef = useRef(null);

    // Fire a pageview whenever the pathname changes AND consent is granted.
    useEffect(() => {
        if (!hasAnalyticsConsent()) return;
        const path = location.pathname + location.search;
        if (lastPathRef.current === path) return;
        lastPathRef.current = path;
        const sid = getSessionId();
        beacon("pageview", {
            session_id: sid,
            url: location.pathname,
            referrer: (typeof document !== "undefined" ? document.referrer : "") || "",
            locale: (typeof navigator !== "undefined" ? navigator.language : "") || undefined,
            screen_resolution: (typeof window !== "undefined"
                ? `${window.screen.width}x${window.screen.height}` : undefined),
        });
    }, [location.pathname, location.search, user]);

    const trackEvent = useCallback((event_name, properties = {}) => {
        if (!hasAnalyticsConsent()) return;
        const sid = getSessionId();
        beacon("event", { session_id: sid, event_name, properties });
    }, []);

    // F9.7 WAVE 2H · forward lock-funnel CustomEvents to the dedicated
    // ingestion endpoint. Fires on both `glms:lock_hover` (intent) and
    // `glms:lock_cta_click` (conversion). Shared session_id with
    // pageviews/events so funnel joins work out of the box.
    // Listener is always registered; the consent gate inside `beacon()`
    // takes care of dropping events when analytics consent is missing.
    useEffect(() => {
        const handler = (ev) => {
            const d = ev?.detail || {};
            if (!d.module || !d.from_group || !d.required_plan) return;
            const action = d.action || (ev.type === "glms:lock_cta_click" ? "cta_click" : "hover");
            const sid = getSessionId();
            beacon("lock-events", {
                session_id: sid,
                module: d.module,
                from_group: d.from_group,
                required_plan: d.required_plan,
                action,
                item_id: d.item_id,
            });
        };
        window.addEventListener("glms:lock_hover", handler);
        window.addEventListener("glms:lock_cta_click", handler);
        return () => {
            window.removeEventListener("glms:lock_hover", handler);
            window.removeEventListener("glms:lock_cta_click", handler);
        };
    }, []);

    return (
        <TrackerContext.Provider value={{ trackEvent }}>
            {children}
        </TrackerContext.Provider>
    );
}
